import { ColumnDef } from "@tanstack/react-table";
import { Box, HStack, Button, Flex } from "@chakra-ui/react";
import moment from "moment-timezone";
import { Task } from "features/Resources/Task/models";
import { CLOSED_TASK_STATUSES } from "features/Resources/Task/constants";
import { taskDisplayTimezone } from "features/Resources/Task/helpers";
import { Text12 } from "components/Text";
import IssueTableIcon from "components/Icon/IssueTableIcon";
import InlineTaskStatusSelector from "features/Resources/Task/Components/Selector/InlineTaskStatusSelector";
import TaskThreeDotOptionMenu from "features/Resources/Task/Components/Button/TaskThreeDotOptionMenu";
import { DueTag } from "features/Resources/Task/Components/StartOrDueDateTag";
import { TaskActionRowButton } from "features/Resources/Task/Components/Action/Button";
import { createColumnHelpers } from "utils/tableColumnHelpers";
import NewHireJourneyTag from "features/NewHireJourney/Components/NewHireJourneyTag";

const column = createColumnHelpers<Task>();

export function createV2TaskColumns(
  onExpand: (item: Task) => void,
  onOpenRemind?: (task: Task) => void,
  showTaskActionButtons?: boolean,
  showJourney?: boolean,
): ColumnDef<Task, any>[] {
  return [
    column.create({
      id: "startOn",
      header: "Start On",
      fixedWidth: 100,
      align: "flex-start",
      cell: ({ getValue }) => {
        const task = getValue();
        const startSource = task.startOn ?? task.effectiveStartDate;
        if (!startSource) return null;
        return (
          <Text12 color="content.tertiary" whiteSpace="nowrap">
            {moment.tz(String(startSource), taskDisplayTimezone(task)).format("MMM D")}
          </Text12>
        );
      },
    }),
    column.create({
      id: "issues",
      fixedWidth: 52,
      padding: "left-only",
      align: "flex-start",
      cell: ({ getValue }) => {
        const task = getValue();
        const showIssues = !CLOSED_TASK_STATUSES.includes(task.status);
        return (
          <Flex align="center">
            {showIssues ? <IssueTableIcon issueIds={task.issueIds} /> : <Box />}
          </Flex>
        );
      },
    }),
    column.create({
      header: "Name",
      flexWidth: { min: 100, max: 400 },
      padding: "right-only",
      align: "flex-start",
      cell: ({ getValue }) => {
        const task = getValue();
        return (
          <Button variant="unstyled" width="fit-content" onClick={() => onExpand(task)}>
            <Text12
              wordBreak="break-word"
              textDecorationLine={task.status === "skipped" ? "line-through" : "none"}
              fontWeight="medium"
              noOfLines={2}
            >
              {task.name}
            </Text12>
          </Button>
        );
      },
    }),
    column.create({
      id: "assignee",
      header: "Assignee",
      fixedWidth: 160,
      align: "flex-start",
      cell: ({ getValue }) => {
        const task = getValue();
        const assigneeName = task.assignee?.fullName || "Unassigned";
        return (
          <Text12 fontWeight="medium" noOfLines={1}>
            {assigneeName}
          </Text12>
        );
      },
    }),
    column.create({
      id: "status",
      header: "Status",
      fixedWidth: 96,
      padding: "none",
      cell: ({ getValue }) => {
        const task = getValue();
        return (
          <HStack spacing={2} alignItems="center" justify="flex-start">
            <InlineTaskStatusSelector taskId={task.id} currentStatus={task.status} />
          </HStack>
        );
      },
    }),
    ...(showJourney
      ? [
        column.create({
          id: "journey",
          header: "Journey",
          fixedWidth: 160,
          align: "flex-start",
          cell: ({ getValue }) => {
            const task = getValue();
            if (!task.newHireJourneyId) return null;
            return (
              <NewHireJourneyTag
                journeyId={task.newHireJourneyId}
                size="md"
                clickable
              />
            );
          },
        }),
      ]
      : []),
    column.create({
      id: "dates",
      header: "",
      fixedWidth: 160,
      padding: "right-only",
      align: "flex-start",
      cell: ({ getValue }) => {
        const task = getValue();
        return (
          <DueTag task={task} />
        );
      },
    }),
    column.spacer(),
    ...(showTaskActionButtons
      ? [
        column.action({
          id: "taskActions",
          fixedWidth: 140,
          cell: ({ getValue }) => {
            const task = getValue();
            return (
              <HStack spacing={2} justify="flex-end">
                <TaskActionRowButton task={task} showMarkComplete />
              </HStack>
            );
          },
        }),
      ]
      : []),
    column.action({
      id: "statusActions",
      fixedWidth: 99,
      cell: ({ getValue }) => {
        const task = getValue();
        return (
          <HStack gap="1">
            <TaskThreeDotOptionMenu
              task={task}
              exclude={["view", "view_template"]}
              onOpenRemind={onOpenRemind ? () => onOpenRemind(task) : undefined}
            />
          </HStack>
        );
      },
    }),
  ];
}
